import { useCallback } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { setCustomFilterDrawer } from '../../store/investor/action';

interface UseClearFilteredViewProps {
    source: string;
    filterSourceKey?: string;
}

interface UseClearFilteredViewReturn {
    clearFilteredView: () => void;
}

/**
 * Custom hook to manage clearing of filtered views in a consistent way
 * across components. This replaces direct window object access patterns.
 */
const useClearFilteredView = ({ source, filterSourceKey }: UseClearFilteredViewProps): UseClearFilteredViewReturn => {
    const dispatch = useAppDispatch();

    const getFilterSourceKey = useCallback(() => {
        if (filterSourceKey) return filterSourceKey;

        // Map source to appropriate filter key
        switch (source) {
            case 'LEADS':
                return 'LEADS_PAGE';
            case 'DEALS':
            case 'DEALS_VIEW':
                return 'DEALS';
            case 'MANDATE_VIEW':
            case 'DEAL_VIEW':
                return 'DEALS'; // Use same sourceKey for both views
            default:
                return 'LEADS_PAGE'; // fallback
        }
    }, [source, filterSourceKey]);

    const clearFilteredView = useCallback(() => {
        const sourceKey = getFilterSourceKey();

        // Clear the filters in Redux store
        dispatch(
            setCustomFilterDrawer({
                open: false,
                appliedFilters: {
                    [sourceKey]: {},
                },
            }),
        );

        // Clear the selected filtered view via the FilteredViewsButton component
        if ((window as any).clearFilteredView) {
            (window as any).clearFilteredView();
        }
    }, [dispatch, getFilterSourceKey]);

    return {
        clearFilteredView,
    };
};

export default useClearFilteredView;
